import {
  BET_STATE,
  BET_MODE,
  TOGGLE,
  AUTOBET_CYCLE,
  ACTION_VARIANT,
  AUTO_CASHOUT,
} from '@constants/bettingConstants'
import { GAME_STATUS } from '@sockets/socketEvents'

const { ENABLED, DISABLED } = TOGGLE
const bool = (on) => (on ? ENABLED : DISABLED)

// A bet has reached a terminal state this round (won or crashed). Controls
// unlock here so the player can prep the next bet immediately.
const isTerminal = (betState) =>
  betState === BET_STATE.BET_CASHED_OUT || betState === BET_STATE.CRASHED

// The bet exists ONLY in frontend state — PLACE_BET has not been sent, so it is
// still entirely the player's: cancelable, re-stakeable, replaceable.
const isPending = (betState) => betState === BET_STATE.BET_PLACED

// PLACE_BET has been sent (or already acked). The bet is FINAL from here on:
// every control that could alter or withdraw it is locked out.
const isCommitted = (betState) =>
  betState === BET_STATE.BET_SUBMITTED ||
  betState === BET_STATE.BET_ACCEPTED ||
  betState === BET_STATE.BET_RUNNING

// Decide the action button's variant + whether it's pressable. Pure.
const resolveActionButton = ({
  betState,
  mode,
  cycle,
  pending,
  status,
  numericStake,
  bounds,
  balance,
  autoCashoutOk,
}) => {
  const isAuto = mode === BET_MODE.AUTO
  const autoLooping = isAuto && cycle === AUTOBET_CYCLE.ACTIVE
  // terminal states (won / crashed) behave like idle for the button, so the
  // next bet can be queued the instant a bet resolves — no wait for the reset.
  const idleish = betState === BET_STATE.IDLE || isTerminal(betState)

  let variant
  if (betState === BET_STATE.BET_RUNNING) {
    variant = ACTION_VARIANT.CASHOUT
  } else if (betState === BET_STATE.BET_SUBMITTED || betState === BET_STATE.BET_ACCEPTED) {
    // sent to the server — nothing to offer but "you're in this round"
    variant = ACTION_VARIANT.PLACED
  } else if (isPending(betState)) {
    variant = ACTION_VARIANT.CANCEL
  } else if (autoLooping) {
    // AUTO loop active but idle between rounds — button STOPS the loop
    variant = ACTION_VARIANT.CANCEL
  } else if (isAuto) {
    variant = ACTION_VARIANT.START_AUTO
  } else {
    variant = ACTION_VARIANT.BET
  }

  let enabled
  if (variant === ACTION_VARIANT.CASHOUT) {
    enabled = !pending // one cashout at a time
  } else if (variant === ACTION_VARIANT.PLACED) {
    enabled = false // the bet is locked in — the button is a badge, not an action
  } else if (variant === ACTION_VARIANT.CANCEL) {
    // A pending bet is ALWAYS cancelable: it never left the client, so clearing
    // it is pure frontend state with nothing to undo server-side. An idle auto
    // loop can likewise be stopped.
    enabled = isPending(betState) || (idleish && autoLooping && !pending)
  } else {
    // BET or START_AUTO — can queue whenever idle-ish, affordable, not
    // maintenance, and (if auto-cashout is on) with a valid multiplier target.
    enabled =
      !pending &&
      idleish &&
      numericStake >= bounds.min &&
      autoCashoutOk &&
      balance >= numericStake &&
      status !== GAME_STATUS.UNDER_MAINTENANCE &&
      status !== null
  }

  return { control: bool(enabled), variant }
}

/**
 * The SINGLE place that decides what the player can interact with, per panel.
 * Everything is a pure function of the source state, so the controls can never
 * desync. Add a new rule by editing this function only.
 *
 * @param panel   the per-panel slice state
 * @param round   state.events.round
 * @param bounds  { min, max, step, maxDecimals }
 * @param balance state.wallet.balance
 * @returns interaction-control shape (all ENABLED|DISABLED) + actionVariant
 */
export const deriveInteractionControls = ({ panel, round, bounds, balance }) => {
  const { betState, mode, pending, stake, autoCashout } = panel
  const status = round?.status ?? null
  const numericStake = Number(stake) || 0

  // When auto-cashout is on, the target multiplier must be within [MIN, MAX] to
  // place a bet — an empty/sub-1.01/over-max value disables the action button,
  // mirroring how a sub-minimum stake does. Off = no constraint.
  const autoCashoutOk =
    autoCashout.status !== TOGGLE.ENABLED ||
    (Number(autoCashout.value) >= AUTO_CASHOUT.MIN && Number(autoCashout.value) <= AUTO_CASHOUT.MAX)

  // Editing is allowed whenever the bet is not yet committed to the server: when
  // idle, the moment a bet settles/crashes (so the next stake can be set right
  // away), AND while a bet is merely PENDING — re-staking a pending bet simply
  // changes what will be submitted, since the emit reads the stake at send time.
  const canEdit = !isCommitted(betState) && !pending

  const action = resolveActionButton({
    betState,
    mode,
    cycle: panel.autoBet.cycle,
    pending,
    status,
    numericStake,
    bounds,
    balance,
    autoCashoutOk,
  })

  return {
    stakeControlKeys: {
      plusKey: bool(canEdit && numericStake < bounds.max),
      minusKey: bool(canEdit && numericStake > bounds.min),
    },
    stakeInput: bool(canEdit),
    quickStakes: bool(canEdit),
    autoBet: bool(canEdit),
    autoCashout: bool(canEdit),
    actionButton: action.control, // ENABLED | DISABLED
    actionVariant: action.variant, // BET | START_AUTO | CANCEL | PLACED | CASHOUT
  }
}
